/* iter-p4-sync — Reader Sync Check admin panel. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCircle2, AlertCircle, PlayCircle } from 'lucide-react';
import ReconcilePreviewDialog from './ReconcilePreviewDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function ReaderSyncPanel() {
  const [state, setState] = useState({ loading: true, report: null, history: [] });
  const [running, setRunning] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [latest, hist] = await Promise.all([
        http.get('/admin/companion/sync-report'),
        http.get('/admin/companion/sync-report/history?limit=10'),
      ]);
      setState({ loading: false,
                   report: latest.data.has_report ? latest.data.report : null,
                   history: hist.data.reports || [] });
    } catch (e) {
      setState({ loading: false, report: null, history: [] });
    }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await http.post('/admin/companion/sync-report/run-now');
      toast.success(r.data.report.healthy ? 'Sync check complete · catalogs healthy ✓' :
                     `Sync run complete — ${r.data.report.counts.drifted} drifted, ${r.data.report.counts.missing_in_reader} missing on Reader.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed. Is COMPANION set?');
    } finally { setRunning(false); }
  };

  const reconcile = async () => {
    setShowReconcile(true);
  };

  if (state.loading) return (
    <div className="flex h-32 items-center justify-center text-[12px] text-muted-foreground">
      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading Reader Sync…
    </div>
  );
  const r = state.report;

  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid="reader-sync-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[13px] font-semibold">Reader Sync Check · Loom ↔ Reader catalog</div>
        <div className="flex gap-1.5">
          <button type="button" onClick={load}
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
          <button type="button" onClick={runNow} disabled={running}
                  data-testid="reader-sync-run"
                  className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <PlayCircle className="h-3 w-3" />}
            Run now
          </button>
          {r && !r.healthy && r.counts.drifted > 0 && (
            <button type="button" onClick={reconcile} disabled={running}
                    data-testid="reader-sync-reconcile"
                    className="inline-flex items-center gap-1 rounded-sm border border-amber-500/60 bg-amber-500/10 px-2 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-500/20 dark:text-amber-200 disabled:opacity-60">
              Push Loom → Reader ({r.counts.drifted})
            </button>
          )}
        </div>
      </div>

      {!r ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">
          No sync has run yet. Click "Run now" or wait for the nightly cron at 03:00 UTC.
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-2 text-[12px]">
            {r.healthy
              ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 font-semibold text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Catalogs healthy</span>
              : <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 font-semibold text-amber-800 dark:text-amber-200"><AlertCircle className="h-3 w-3" /> Drift detected</span>}
            <span className="text-muted-foreground">·</span>
            <span className="font-mono text-[10.5px] text-muted-foreground">{(r.computed_at || '').slice(0, 16).replace('T', ' ')}</span>
          </div>
          <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4 text-[12px]" data-testid="reader-sync-counts">
            <Chip label="Loom books"     value={r.loom_count} />
            <Chip label="Reader books"   value={r.reader_count} />
            <Chip label="Drifted"        value={r.counts.drifted} tone={r.counts.drifted ? 'warn' : 'ok'} />
            <Chip label="Missing (either side)" value={r.counts.missing_in_reader + r.counts.missing_in_loom}
                  tone={(r.counts.missing_in_reader + r.counts.missing_in_loom) ? 'warn' : 'ok'} />
          </div>
          {r.drifted.length > 0 && (
            <details className="mb-2">
              <summary className="cursor-pointer text-[11.5px] font-semibold">Drifted ({r.drifted.length})</summary>
              <ul className="mt-1 space-y-1 text-[11px]">
                {r.drifted.slice(0, 20).map((d, i) => (
                  <li key={i} className="rounded-sm border border-border p-2">
                    <div className="font-semibold">{d.title} · <span className="font-mono text-muted-foreground">{d.isbn || d.id}</span></div>
                    <ul className="mt-1 space-y-0.5">
                      {d.drift.map((f, j) => (
                        <li key={j} className="font-mono text-[10.5px]">
                          {f.field}: <span className="text-red-600">Loom {String(f.loom)}</span> ≠ <span className="text-amber-700">Reader {String(f.reader)}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {r.missing_in_reader.length > 0 && (
            <details className="mb-2">
              <summary className="cursor-pointer text-[11.5px] font-semibold">Missing on Reader ({r.missing_in_reader.length})</summary>
              <ul className="mt-1 space-y-0.5 text-[11px]">
                {r.missing_in_reader.slice(0, 20).map((m, i) => (
                  <li key={i}><b>{m.title}</b> · <span className="font-mono text-muted-foreground">{m.isbn || m.id}</span></li>
                ))}
              </ul>
            </details>
          )}
          {r.missing_in_loom.length > 0 && (
            <details className="mb-2">
              <summary className="cursor-pointer text-[11.5px] font-semibold">Extra on Reader ({r.missing_in_loom.length})</summary>
              <ul className="mt-1 space-y-0.5 text-[11px]">
                {r.missing_in_loom.slice(0, 20).map((m, i) => (
                  <li key={i}><b>{m.title || '(untitled)'}</b> · <span className="font-mono text-muted-foreground">{m.isbn || m.reader_id}</span></li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      {state.history.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">Recent runs</div>
          <table className="w-full text-[11.5px]">
            <thead><tr className="text-left text-[10px] uppercase text-muted-foreground">
              <th className="py-1">When</th><th>Healthy</th><th>Drifted</th><th>Missing</th>
            </tr></thead>
            <tbody>
              {state.history.map((h, i) => (
                <tr key={i} className="border-b border-border/60">
                  <td className="py-1 font-mono text-[10.5px]">{(h.computed_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td>{h.healthy ? '✓' : '✗'}</td>
                  <td>{h.counts?.drifted ?? 0}</td>
                  <td>{(h.counts?.missing_in_reader ?? 0) + (h.counts?.missing_in_loom ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showReconcile && (
        <ReconcilePreviewDialog
          http={http}
          drifted={r?.drifted || []}
          onClose={() => setShowReconcile(false)}
          onDone={async () => { await load(); setTimeout(() => setShowReconcile(false), 800); }}
        />
      )}
    </section>
  );
}

function Chip({ label, value, tone }) {
  const cls = tone === 'warn'
    ? 'border-amber-500/40 bg-amber-500/10'
    : tone === 'ok'
      ? 'border-emerald-500/40 bg-emerald-500/10'
      : 'border-border bg-panel-header/40';
  return (
    <div className={`rounded-md border p-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-[13.5px] font-semibold tracking-tight">{value}</div>
    </div>
  );
}
